'use client'

import {
    getPaymentAccount,
    PaymentAccount,
    syncPaymentAccountBalance,
    syncPaymentAccountStatus,
} from '@/lib/api/financial/payment-account-api'
import {
    Anticipation,
    BillPayment,
    ExtractItem,
    listAnticipations,
    listBillPayments,
    listMobileRecharges,
    listPixKeys,
    listTransfers,
    MobileRecharge,
    PixKey,
    TransferItem,
    BalanceOverview,
    getExtract,
    getBalanceOverview,
    syncExtract,
} from '@/lib/api/financial/digital-account-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { resolveOperationLabel } from '@/lib/financial/ledger-translations'
import { SocketContext } from '@/lib/providers/socket-provider'
import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    MdAccountBalanceWallet,
    MdAttachMoney,
    MdCheckCircle,
    MdHourglassEmpty,
    MdLock,
    MdShield,
    MdPayments,
    MdPhoneAndroid,
    MdPix,
    MdQrCode2,
    MdQrCodeScanner,
    MdReceiptLong,
    MdRefresh,
    MdSend,
    MdSwapHoriz,
    MdTrendingUp,
    MdVisibility,
    MdVisibilityOff,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import SendPixModal from './components/send-pix-modal'
import SendTedModal from './components/send-ted-modal'
import ReceivePixModal from './components/receive-pix-modal'
import PixKeysModal from './components/pix-keys-modal'
import RechargeModal from './components/recharge-modal'
import PayBillModal from './components/pay-bill-modal'
import OperatorsModal from './components/operators-modal'
import DocumentsSection from './components/documents-section'
import ActivationWizard from './components/activation-wizard'
import PaymentCodeScanner from '@/components/code-scanner/payment-code-scanner'

const PENDING_TRANSFER_STATUSES = new Set([
    'PENDING',
    'AWAITING_APPROVAL',
    'AWAITING_CONFIRMATION',
])

const STATUS_LABEL: Record<string, string> = {
    PENDING_KYC: 'Aguardando análise',
    ACTIVE: 'Ativa',
    BLOCKED: 'Bloqueada',
    CLOSED: 'Encerrada',
}

// Tradução PT-BR para o tipo de movimentação. Cobre tanto o nosso enum
// LedgerEntryType quanto o tipo nativo do Asaas (raw.asaasType) quando o
// nosso ficou em ADJUSTMENT — assim o front mostra o rótulo mais fiel.
const translateType = (item: {
    type: string
    asaasType: string | null
}): string =>
    resolveOperationLabel({ type: item.type, asaasType: item.asaasType })

const STATUS_BADGE: Record<string, string> = {
    PENDING_KYC: 'badge-warning',
    ACTIVE: 'badge-success',
    BLOCKED: 'badge-error',
    CLOSED: 'badge-ghost',
}

const formatCurrency = (cents: number): string =>
    (cents / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    })

const formatBrl = (value: number): string =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const formatDate = (iso: string | null | undefined): string =>
    iso
        ? new Date(iso).toLocaleString('pt-BR', {
              timeZone: 'America/Sao_Paulo',
          })
        : '—'

const formatDateShort = (iso: string | null | undefined): string =>
    iso
        ? new Date(iso).toLocaleDateString('pt-BR', {
              timeZone: 'America/Sao_Paulo',
          })
        : '—'

type Tab =
    | 'extract'
    | 'pix'
    | 'transfers'
    | 'recharges'
    | 'bills'
    | 'anticipations'
    | 'documents'

export default function PaymentAccountPage() {
    const { canCreate, canUpdate } = useResourcePermissions(
        'financial',
        'payment_account'
    )
    const { user } = useAuth()
    const { socket } = useContext(SocketContext)
    const navigate = useNavigate()
    const companyName =
        user?.company?.tradingName || user?.company?.name || 'Sua empresa'

    const [account, setAccount] = useState<PaymentAccount | null>(null)
    const [overview, setOverview] = useState<BalanceOverview | null>(null)
    const [loading, setLoading] = useState(true)
    const [syncing, setSyncing] = useState(false)
    const [hideBalance, setHideBalance] = useState(false)

    const [tab, setTab] = useState<Tab>('extract')

    const [extract, setExtract] = useState<ExtractItem[]>([])
    const [extractPage, setExtractPage] = useState(1)
    const [extractPages, setExtractPages] = useState(1)
    const [syncingExtract, setSyncingExtract] = useState(false)
    const [pixKeys, setPixKeys] = useState<PixKey[]>([])
    const [transfers, setTransfers] = useState<TransferItem[]>([])
    const [recharges, setRecharges] = useState<MobileRecharge[]>([])
    const [bills, setBills] = useState<BillPayment[]>([])
    const [anticipations, setAnticipations] = useState<Anticipation[]>([])
    const [loadingTab, setLoadingTab] = useState(false)

    const [showSendPix, setShowSendPix] = useState(false)
    const [showSendTed, setShowSendTed] = useState(false)
    const [showReceivePix, setShowReceivePix] = useState(false)
    const [showPixKeys, setShowPixKeys] = useState(false)
    const [showRecharge, setShowRecharge] = useState(false)
    const [showPayBill, setShowPayBill] = useState(false)
    const [showScanner, setShowScanner] = useState(false)
    // Código lido pela câmera na ação "Pagar com câmera", entregue ao modal certo.
    const [scannedPixPayload, setScannedPixPayload] = useState<string>()
    const [scannedBillLine, setScannedBillLine] = useState<string>()
    const [showOperators, setShowOperators] = useState(false)

    useEffect(() => {
        load()
    }, [])

    const load = async () => {
        setLoading(true)
        try {
            const res = await getPaymentAccount()
            if (res.success && res.result) setAccount(res.result)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (!account?.paymentAccountId) return
        loadTab(tab)
    }, [tab, account?.paymentAccountId, extractPage])

    useEffect(() => {
        if (!account?.paymentAccountId) return
        listPixKeys().then(k => {
            if (k.success && k.result) setPixKeys(k.result.items)
        })
    }, [account?.paymentAccountId])

    // Painel de saldos (disponível/a liberar/a receber/vencido) só existe com a
    // conta ativa. Re-busca quando o saldo sincroniza (webhook -> load).
    useEffect(() => {
        if (account?.paymentAccountStatus !== 'ACTIVE') {
            setOverview(null)
            return
        }
        getBalanceOverview().then(r => {
            if (r.success && r.result) setOverview(r.result)
        })
    }, [account?.paymentAccountStatus, account?.paymentBalanceSyncAt])

    // Escuta eventos do webhook do provedor (push via socket): refaz fetch
    // silencioso do saldo + aba ativa. Sem toast — atualizacao eh passiva.
    useEffect(() => {
        if (!socket || !account?.paymentAccountId) return
        const handler = () => {
            load()
            loadTab(tab)
        }
        socket.on('digital-account:event', handler)
        return () => {
            socket.off('digital-account:event', handler)
        }
    }, [socket, account?.paymentAccountId, tab])

    const loadTab = async (t: Tab) => {
        setLoadingTab(true)
        try {
            if (t === 'extract') {
                const r = await getExtract({ page: extractPage, limit: 25 })
                if (r.success && r.result) {
                    setExtract(r.result.items)
                    setExtractPages(r.result.pages)
                }
            } else if (t === 'pix') {
                const k = await listPixKeys()
                if (k.success && k.result) setPixKeys(k.result.items)
            } else if (t === 'transfers') {
                const r = await listTransfers({ limit: 50 })
                if (r.success && r.result) setTransfers(r.result.items)
            } else if (t === 'recharges') {
                const r = await listMobileRecharges({ limit: 50 })
                if (r.success && r.result) setRecharges(r.result.items)
            } else if (t === 'bills') {
                const r = await listBillPayments({ limit: 50 })
                if (r.success && r.result) setBills(r.result.items)
            } else if (t === 'anticipations') {
                const r = await listAnticipations({ limit: 50 })
                if (r.success && r.result) setAnticipations(r.result.items)
            }
        } finally {
            setLoadingTab(false)
        }
    }

    const handleSyncStatus = async () => {
        setSyncing(true)
        try {
            const res = await syncPaymentAccountStatus()
            if (res.success) {
                toast.success(
                    res.result?.changed
                        ? `Status atualizado: ${res.result.status}`
                        : 'Status já estava atualizado'
                )
                load()
            } else toast.error(res.error || 'Erro ao sincronizar status')
        } finally {
            setSyncing(false)
        }
    }

    const handleSyncBalance = async () => {
        // "Atualizar" sincroniza primeiro o status (libera a conta se o KYC já
        // foi aprovado no provedor, fazendo o banner de pendência sumir) e,
        // quando a conta está ativa, também o saldo. Fallback manual ao
        // webhook/socket.
        setSyncing(true)
        try {
            const statusRes = await syncPaymentAccountStatus()
            if (statusRes.success && statusRes.result?.status === 'ACTIVE') {
                await syncPaymentAccountBalance()
            }
            load()
        } finally {
            setSyncing(false)
        }
    }

    const onAfterAction = () => {
        // Nao chama handleSyncBalance — saldo vem do webhook (socket) quando
        // o provedor confirmar a operacao. So refaz a aba ativa pra mostrar
        // a operacao recem criada (que ja existe no provider).
        loadTab(tab)
    }

    const handleSyncExtract = async () => {
        setSyncingExtract(true)
        try {
            const r = await syncExtract({})
            if (r.success && r.result) {
                toast.success(
                    `Extrato sincronizado: ${r.result.imported} novas, ${r.result.updated} atualizadas`
                )
                setExtractPage(1)
                loadTab('extract')
            } else {
                toast.error(r.error || 'Erro ao sincronizar extrato')
            }
        } finally {
            setSyncingExtract(false)
        }
    }

    // Disparado por mudanças na aba Documentação (upload/remoção/atualizar):
    // sincroniza o status com o provedor — pode liberar a conta (ACTIVE) e
    // fazer o banner de pendência sumir — e recarrega o estado.
    const handleDocumentsChanged = async () => {
        await syncPaymentAccountStatus().catch(() => undefined)
        load()
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
            </div>
        )
    }

    const isActivated = !!account?.paymentAccountId

    if (!isActivated) {
        return (
            <div className="p-4 md:p-6 max-w-4xl mx-auto">
                {canCreate ? (
                    <ActivationWizard onActivated={load} />
                ) : (
                    <div className="alert">
                        Conta digital ainda não ativada para esta empresa.
                    </div>
                )}
            </div>
        )
    }

    const statusKey = account.paymentAccountStatus ?? ''
    // Movimentação só é liberada com a conta ACTIVE. Enquanto estiver em
    // análise/bloqueada, as ações ficam desabilitadas.
    const accountActive = statusKey === 'ACTIVE'
    const actions = [
        {
            key: 'scan',
            label: 'Pagar com câmera',
            icon: MdQrCodeScanner,
            color: 'from-slate-600 to-slate-800',
            onClick: () => setShowScanner(true),
        },
        {
            key: 'send-pix',
            label: 'Enviar PIX',
            icon: MdPix,
            color: 'from-emerald-500 to-teal-600',
            onClick: () => setShowSendPix(true),
        },
        {
            key: 'receive-pix',
            label: 'Receber',
            icon: MdQrCode2,
            color: 'from-blue-500 to-indigo-600',
            onClick: () => setShowReceivePix(true),
        },
        {
            key: 'pix-keys',
            label: 'Chaves PIX',
            icon: MdAccountBalanceWallet,
            color: 'from-purple-500 to-fuchsia-600',
            onClick: () => setShowPixKeys(true),
        },
        {
            key: 'ted',
            label: 'Transferir',
            icon: MdSwapHoriz,
            color: 'from-cyan-500 to-blue-600',
            onClick: () => setShowSendTed(true),
        },
        {
            key: 'recharge',
            label: 'Recarregar',
            icon: MdPhoneAndroid,
            color: 'from-orange-500 to-amber-600',
            onClick: () => setShowRecharge(true),
        },
        {
            key: 'bill',
            label: 'Pagar Conta',
            icon: MdReceiptLong,
            color: 'from-rose-500 to-pink-600',
            onClick: () => setShowPayBill(true),
        },
    ]

    return (
        <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-6 ">
            <div className="rounded-3xl bg-base-100 border border-base-300/60 border-l-accent shadow-sm p-4 md:p-6 rounded-l-xl! relative">
                <p className="text-xs text-base-content/40 mt-2 absolute right-9 top-1 flex items-center gap-5">
                    Atualizado: {formatDate(account.paymentBalanceSyncAt)}{' '}
                </p>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        {/* <div className="flex items-center gap-2 mb-2">
                            <MdAccountBalanceWallet className="text-2xl text-secondary" />
                            <p className="font-medium text-base-content">
                                {companyName}
                            </p>
                            <span
                                className={`badge badge-sm ${STATUS_BADGE[statusKey] || 'badge-ghost'}`}
                            >
                                {STATUS_LABEL[statusKey] || 'Desconhecido'}
                            </span>
                        </div>*/}
                        <div className="flex items-center gap-3">
                            <p className="text-sm text-base-content/60">
                                Saldo disponível
                            </p>
                            <button
                                type="button"
                                onClick={() => setHideBalance(v => !v)}
                                className="text-base-content/50 hover:text-base-content"
                                title={hideBalance ? 'Mostrar' : 'Ocultar'}
                            >
                                {hideBalance ? (
                                    <MdVisibility />
                                ) : (
                                    <MdVisibilityOff />
                                )}
                            </button>
                        </div>
                        <p className="text-2xl md:text-3xl font-bold tracking-tight mt-1 text-base-content">
                            {hideBalance
                                ? '••••••••'
                                : formatCurrency(account.paymentBalanceCache)}
                        </p>
                    </div>

                    <div className="flex flex-wrap gap-2 self-start md:self-auto">
                        <button
                            onClick={handleSyncBalance}
                            disabled={syncing}
                            className="btn btn-sm btn-ghost"
                        >
                            <MdRefresh /> Atualizar
                        </button>
                        <button
                            onClick={handleSyncStatus}
                            disabled={syncing}
                            className="btn btn-sm btn-ghost"
                        >
                            <MdSync /> Status
                        </button>
                        <button
                            onClick={() => setShowOperators(true)}
                            className="btn btn-sm btn-ghost"
                        >
                            <MdShield /> Operadores
                        </button>
                        <img
                            src="https://baas.asaas.com/selos/Servicos_financeiros_Asaas-Reduzida-Positivo.svg?id=d6866c01-f11b-417d-b544-09d572b65e19"
                            alt="Asaas"
                            width={90}
                            className=" object-contain"
                        />
                    </div>
                </div>
            </div>
            {/* PAINEL DE SALDOS COMPLEMENTARES */}
            {overview && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <OverviewCard
                        icon={MdCheckCircle}
                        label="A liberar"
                        hint="Confirmado, aguardando crédito"
                        value={overview.confirmedCents}
                        accent="text-info"
                        hidden={hideBalance}
                    />
                    <OverviewCard
                        icon={MdHourglassEmpty}
                        label="A receber"
                        hint="Cobranças em aberto"
                        value={overview.pendingCents}
                        accent="text-warning"
                        hidden={hideBalance}
                    />
                    <OverviewCard
                        icon={MdReceiptLong}
                        label="Vencido"
                        hint="Cobranças vencidas"
                        value={overview.overdueCents}
                        accent="text-error"
                        hidden={hideBalance}
                    />
                </div>
            )}
            {/* BANNER DE STATUS PENDENTE / KYC */}
            {statusKey !== 'ACTIVE' && (
                <div
                    className={`rounded-2xl border p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${
                        statusKey === 'BLOCKED' || statusKey === 'CLOSED'
                            ? 'border-error/40 bg-error/5'
                            : 'border-warning/40 bg-warning/5'
                    }`}
                >
                    <div className="flex items-start gap-3 flex-1">
                        {statusKey === 'BLOCKED' || statusKey === 'CLOSED' ? (
                            <MdLock className="text-2xl text-error flex-shrink-0 mt-0.5" />
                        ) : (
                            <MdHourglassEmpty className="text-2xl text-warning flex-shrink-0 mt-0.5" />
                        )}
                        <div>
                            <p className="font-semibold text-sm">
                                Conta {STATUS_LABEL[statusKey] || 'pendente'} —
                                movimentação ainda não liberada
                            </p>
                            <p className="text-xs text-base-content/60 mt-0.5">
                                {statusKey === 'BLOCKED' ||
                                statusKey === 'CLOSED'
                                    ? 'Verifique as pendências de documentação e reenvie os documentos reprovados.'
                                    : 'Envie os documentos solicitados pelo provedor para liberar PIX, transferências e cobranças.'}
                            </p>
                        </div>
                    </div>
                    <button
                        className="btn btn-sm btn-secondary self-start sm:self-auto"
                        onClick={() => setTab('documents')}
                    >
                        <MdShield /> Enviar documentos
                    </button>
                </div>
            )}
            {/* AÇÕES RÁPIDAS */}
            {accountActive && (
                <div className="grid grid-cols-4 md:grid-cols-7 gap-2 md:gap-3">
                    {actions.map(a => (
                        <button
                            key={a.key}
                            type="button"
                            onClick={a.onClick}
                            disabled={!canUpdate || !accountActive}
                            title={
                                !accountActive
                                    ? 'Disponível após a conta ser aprovada'
                                    : undefined
                            }
                            className="group flex flex-col items-center gap-2 p-2.5 md:p-4 rounded-2xl bg-base-100 shadow hover:shadow-lg transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow"
                        >
                            <div
                                className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${a.color} flex items-center justify-center text-white shadow-md group-hover:scale-110 transition-transform`}
                            >
                                <a.icon className="text-2xl" />
                            </div>
                            <span className="text-xs font-medium text-base-content/80 text-center leading-tight">
                                {a.label}
                            </span>
                        </button>
                    ))}
                </div>
            )}

            {/* TABS */}
            <div className="bg-base-100 rounded-2xl shadow-sm border border-base-300/60">
                <div className="flex flex-wrap gap-1 px-3 pt-3 border-b border-base-300/60">
                    <TabBtn
                        icon={MdReceiptLong}
                        label="Extrato"
                        active={tab === 'extract'}
                        onClick={() => setTab('extract')}
                    />
                    <TabBtn
                        icon={MdPix}
                        label="PIX"
                        active={tab === 'pix'}
                        onClick={() => setTab('pix')}
                    />
                    <TabBtn
                        icon={MdSwapHoriz}
                        label="Transferências"
                        active={tab === 'transfers'}
                        onClick={() => setTab('transfers')}
                    />
                    <TabBtn
                        icon={MdPhoneAndroid}
                        label="Recargas"
                        active={tab === 'recharges'}
                        onClick={() => setTab('recharges')}
                    />
                    <TabBtn
                        icon={MdPayments}
                        label="Pagamentos"
                        active={tab === 'bills'}
                        onClick={() => setTab('bills')}
                    />
                    <TabBtn
                        icon={MdTrendingUp}
                        label="Antecipações"
                        active={tab === 'anticipations'}
                        onClick={() => setTab('anticipations')}
                    />
                    <TabBtn
                        icon={MdShield}
                        label="Documentação"
                        active={tab === 'documents'}
                        onClick={() => setTab('documents')}
                        highlight={statusKey !== 'ACTIVE'}
                    />
                </div>

                <div className="p-4 md:p-6 min-h-[280px]">
                    {loadingTab ? (
                        <div className="flex justify-center py-12">
                            <span className="loading loading-spinner loading-md text-secondary" />
                        </div>
                    ) : tab === 'extract' ? (
                        <ExtractTable
                            items={extract}
                            page={extractPage}
                            pages={extractPages}
                            onPage={setExtractPage}
                            onSync={handleSyncExtract}
                            syncing={syncingExtract}
                            onSelect={id =>
                                navigate(`/financial/statement/${id}`)
                            }
                        />
                    ) : tab === 'pix' ? (
                        <PixSection
                            keys={pixKeys}
                            onManageKeys={() => setShowPixKeys(true)}
                            onReceive={() => setShowReceivePix(true)}
                        />
                    ) : tab === 'transfers' ? (
                        <TransfersTable items={transfers} />
                    ) : tab === 'recharges' ? (
                        <RechargesTable items={recharges} />
                    ) : tab === 'bills' ? (
                        <BillsTable items={bills} />
                    ) : tab === 'anticipations' ? (
                        <AnticipationsTable items={anticipations} />
                    ) : (
                        <DocumentsSection
                            canUpdate={canUpdate}
                            onChanged={handleDocumentsChanged}
                        />
                    )}
                </div>
            </div>
            {/* MODAIS */}
            {showSendPix && (
                <SendPixModal
                    initialPayload={scannedPixPayload}
                    onClose={() => {
                        setShowSendPix(false)
                        setScannedPixPayload(undefined)
                    }}
                    onSuccess={() => {
                        setShowSendPix(false)
                        setScannedPixPayload(undefined)
                        onAfterAction()
                    }}
                />
            )}
            {showSendTed && (
                <SendTedModal
                    onClose={() => setShowSendTed(false)}
                    onSuccess={() => {
                        setShowSendTed(false)
                        onAfterAction()
                    }}
                />
            )}
            {showReceivePix && (
                <ReceivePixModal
                    pixKeys={pixKeys}
                    onClose={() => setShowReceivePix(false)}
                    onSuccess={() => {
                        setShowReceivePix(false)
                        loadTab('pix')
                    }}
                />
            )}
            {showPixKeys && (
                <PixKeysModal
                    pixKeys={pixKeys}
                    onClose={() => setShowPixKeys(false)}
                    onChange={() => loadTab('pix')}
                />
            )}
            {showRecharge && (
                <RechargeModal
                    onClose={() => setShowRecharge(false)}
                    onSuccess={() => {
                        setShowRecharge(false)
                        onAfterAction()
                    }}
                />
            )}
            {showPayBill && (
                <PayBillModal
                    initialDigitableLine={scannedBillLine}
                    onClose={() => {
                        setShowPayBill(false)
                        setScannedBillLine(undefined)
                    }}
                    onSuccess={() => {
                        setShowPayBill(false)
                        setScannedBillLine(undefined)
                        onAfterAction()
                    }}
                />
            )}
            {showScanner && (
                <PaymentCodeScanner
                    onClose={() => setShowScanner(false)}
                    onDetected={code => {
                        setShowScanner(false)
                        if (code.kind === 'pix') {
                            setScannedPixPayload(code.payload)
                            setShowSendPix(true)
                        } else {
                            setScannedBillLine(code.digitableLine)
                            setShowPayBill(true)
                        }
                    }}
                />
            )}
            {showOperators && (
                <OperatorsModal onClose={() => setShowOperators(false)} />
            )}
        </div>
    )
}

function TabBtn({
    icon: Icon,
    label,
    active,
    onClick,
    highlight,
}: {
    icon: any
    label: string
    active: boolean
    onClick: () => void
    highlight?: boolean
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors -mb-px
                ${
                    active
                        ? 'text-secondary bg-secondary/5 border-b-2 border-secondary'
                        : 'text-base-content/60 hover:text-base-content hover:bg-base-200/60 border-b-2 border-transparent'
                }`}
        >
            <Icon
                className={`text-base ${active ? 'text-secondary' : 'text-base-content/50'}`}
            />
            <span>{label}</span>
            {highlight && (
                <span className="w-2 h-2 rounded-full bg-warning animate-pulse" />
            )}
        </button>
    )
}

function MdSync(props: any) {
    return <MdRefresh {...props} />
}

function OverviewCard({
    icon: Icon,
    label,
    hint,
    value,
    accent,
    hidden,
}: {
    icon: any
    label: string
    hint: string
    value: number
    accent: string
    hidden: boolean
}) {
    return (
        <div className="rounded-2xl bg-base-100 border border-base-300/60 p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
                <Icon className={`text-base ${accent}`} />
                <span className="text-sm font-medium text-base-content/70">
                    {label}
                </span>
            </div>
            <p className={`text-xl font-bold tracking-tight ${accent}`}>
                {hidden ? '••••••' : formatCurrency(value)}
            </p>
            <p className="text-[11px] text-base-content/50 mt-0.5">{hint}</p>
        </div>
    )
}

function Empty({ icon: Icon, message }: { icon: any; message: string }) {
    return (
        <div className="flex flex-col items-center justify-center py-12 text-base-content/40">
            <Icon className="text-5xl mb-2" />
            <p className="text-sm">{message}</p>
        </div>
    )
}

function ExtractTable({
    items,
    page,
    pages,
    onPage,
    onSync,
    syncing,
    onSelect,
}: {
    items: ExtractItem[]
    page: number
    pages: number
    onPage: (p: number) => void
    onSync: () => void
    syncing: boolean
    onSelect: (id: number) => void
}) {
    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-base-content/60">
                    Atualizado em tempo real via webhook. Use Sincronizar apenas
                    para recuperar histórico ou eventos perdidos.
                </p>
                <button
                    className="btn btn-xs btn-ghost"
                    onClick={onSync}
                    disabled={syncing}
                >
                    {syncing ? (
                        <span className="loading loading-spinner loading-xs" />
                    ) : (
                        <MdRefresh />
                    )}
                    Sincronizar
                </button>
            </div>
            {items.length === 0 ? (
                <Empty
                    icon={MdReceiptLong}
                    message="Sem movimentações. Clique em Sincronizar para puxar do provedor."
                />
            ) : (
                <>
                    <div className="overflow-x-auto">
                        <table className="table table-sm">
                            <thead>
                                <tr>
                                    <th>Data</th>
                                    <th>Tipo</th>
                                    <th>Descrição</th>
                                    <th className="text-right">Valor</th>
                                    <th className="text-right">Saldo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map(item => (
                                    <tr
                                        key={item.id}
                                        className="cursor-pointer hover:bg-base-200"
                                        onClick={() => onSelect(item.id)}
                                    >
                                        <td className="text-xs text-base-content/60 whitespace-nowrap">
                                            {formatDate(item.occurredAt)}
                                        </td>
                                        <td>
                                            <span className="badge badge-ghost badge-sm">
                                                {translateType(item)}
                                            </span>
                                        </td>
                                        <td
                                            className="max-w-xs truncate"
                                            title={item.description || ''}
                                        >
                                            {item.description || '—'}
                                        </td>
                                        <td
                                            className={`text-right font-mono whitespace-nowrap ${item.amount < 0 ? 'text-error' : 'text-success'}`}
                                        >
                                            {formatCurrency(item.amount)}
                                        </td>
                                        <td className="text-right font-mono whitespace-nowrap text-base-content/60">
                                            {item.balanceAfter != null
                                                ? formatCurrency(
                                                      item.balanceAfter
                                                  )
                                                : '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {pages > 1 && (
                        <div className="flex items-center justify-between mt-3">
                            <span className="text-xs text-base-content/60">
                                Página {page} de {pages}
                            </span>
                            <div className="flex items-center gap-3">
                                <button
                                    className="btn btn-xs join-item"
                                    disabled={page <= 1}
                                    onClick={() => onPage(page - 1)}
                                >
                                    Anterior
                                </button>
                                <button
                                    className="btn btn-xs join-item"
                                    disabled={page >= pages}
                                    onClick={() => onPage(page + 1)}
                                >
                                    Próxima
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

function PixSection({
    keys,
    onManageKeys,
    onReceive,
}: {
    keys: PixKey[]
    onManageKeys: () => void
    onReceive: () => void
}) {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
                <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold">Chaves PIX</h3>
                    <button
                        className="btn btn-xs btn-ghost"
                        onClick={onManageKeys}
                    >
                        Gerenciar
                    </button>
                </div>
                {keys.length === 0 ? (
                    <Empty
                        icon={MdAccountBalanceWallet}
                        message="Nenhuma chave cadastrada"
                    />
                ) : (
                    <ul className="space-y-2">
                        {keys.map(k => (
                            <li
                                key={k.id}
                                className="flex items-center justify-between p-2 rounded-lg bg-base-200/40"
                            >
                                <div className="min-w-0">
                                    <span className="badge badge-sm mr-2">
                                        {k.type}
                                    </span>
                                    <span className="text-sm font-mono truncate">
                                        {k.key}
                                    </span>
                                </div>
                                <span className="badge badge-xs">
                                    {k.status}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            <div>
                <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold">QR Codes estáticos</h3>
                    <button
                        className="btn btn-xs btn-secondary"
                        onClick={onReceive}
                    >
                        Gerar QR
                    </button>
                </div>
                {/*
                    O provedor não guarda histórico consultável de QR estático:
                    o QR é entregue no momento em que é gerado (imagem,
                    copia-e-cola e envio por e-mail no próprio modal).
                */}
                <Empty
                    icon={MdQrCode2}
                    message="Gere um QR Code para receber por PIX. A imagem e o copia-e-cola aparecem na hora, prontos para enviar."
                />
            </div>
        </div>
    )
}

function TransfersTable({ items }: { items: TransferItem[] }) {
    if (items.length === 0)
        return <Empty icon={MdSwapHoriz} message="Sem transferências" />
    return (
        <div className="overflow-x-auto">
            <table className="table table-sm">
                <thead>
                    <tr>
                        <th>Data</th>
                        <th>Tipo</th>
                        <th>Descrição</th>
                        <th>Status</th>
                        <th className="text-right">Valor</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map(item => {
                        const pending = PENDING_TRANSFER_STATUSES.has(
                            item.status
                        )
                        return (
                            <tr key={item.id}>
                                <td className="text-xs text-base-content/60">
                                    {formatDate(item.transferDate)}
                                </td>
                                <td>
                                    <span className="badge badge-ghost badge-sm">
                                        {item.operationType}
                                    </span>
                                </td>
                                <td>{item.description || '—'}</td>
                                <td>
                                    <span
                                        className={`badge badge-sm ${pending ? 'badge-warning' : ''}`}
                                    >
                                        {item.status}
                                    </span>
                                </td>
                                <td className="text-right font-mono">
                                    {formatBrl(item.value)}
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}

function RechargesTable({ items }: { items: MobileRecharge[] }) {
    if (items.length === 0)
        return <Empty icon={MdPhoneAndroid} message="Sem recargas" />
    return (
        <div className="overflow-x-auto">
            <table className="table table-sm">
                <thead>
                    <tr>
                        <th>Data</th>
                        <th>Operadora</th>
                        <th>Telefone</th>
                        <th>Status</th>
                        <th className="text-right">Valor</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map(item => (
                        <tr key={item.id}>
                            <td className="text-xs text-base-content/60">
                                {formatDate(item.rechargeDate)}
                            </td>
                            <td>{item.operator}</td>
                            <td className="font-mono text-xs">
                                {item.phoneNumber}
                            </td>
                            <td>
                                <span className="badge badge-sm">
                                    {item.status}
                                </span>
                            </td>
                            <td className="text-right font-mono">
                                {formatBrl(item.value)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

function BillsTable({ items }: { items: BillPayment[] }) {
    if (items.length === 0)
        return <Empty icon={MdPayments} message="Sem pagamentos de contas" />
    return (
        <div className="overflow-x-auto">
            <table className="table table-sm">
                <thead>
                    <tr>
                        <th>Data</th>
                        <th>Descrição</th>
                        <th>Vencimento</th>
                        <th>Status</th>
                        <th className="text-right">Valor</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map(item => (
                        <tr key={item.id}>
                            <td className="text-xs text-base-content/60">
                                {formatDate(item.paymentDate)}
                            </td>
                            <td>{item.description || '—'}</td>
                            <td>{formatDateShort(item.dueDate)}</td>
                            <td>
                                <span className="badge badge-sm">
                                    {item.status}
                                </span>
                            </td>
                            <td className="text-right font-mono">
                                {formatBrl(item.value)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

function AnticipationsTable({ items }: { items: Anticipation[] }) {
    if (items.length === 0)
        return (
            <Empty
                icon={MdTrendingUp}
                message="Nenhuma antecipação solicitada"
            />
        )
    return (
        <div className="overflow-x-auto">
            <table className="table table-sm">
                <thead>
                    <tr>
                        <th>Data</th>
                        <th>Status</th>
                        <th className="text-right">Bruto</th>
                        <th className="text-right">Taxa</th>
                        <th className="text-right">Líquido</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map(item => (
                        <tr key={item.id}>
                            <td className="text-xs text-base-content/60">
                                {formatDate(item.requestDate)}
                            </td>
                            <td>
                                <span className="badge badge-sm">
                                    {item.status}
                                </span>
                            </td>
                            <td className="text-right font-mono">
                                {formatBrl(item.value)}
                            </td>
                            <td className="text-right font-mono text-error">
                                -{formatBrl(item.fee)}
                            </td>
                            <td className="text-right font-mono text-success">
                                {formatBrl(item.netValue)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}
